import { Button } from 'antd';
import Modal from 'antd/es/modal';
import FocusTrap from 'focus-trap-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import Sprite from '../../../assets/img/symbol/sprite.svg';
import { APP_STORE_URL } from '../../../common/constants';
import Loader from '../../../components/Loader';
import DialogFooter from '../../../components/account/DialogFooter';
import SendMessageForm from '../../../components/account/forms/SendMessageForm';
import PinnedMessageContainer from '../../../components/pinned-message-container';
import ScrollDownButton from '../../../components/scroll-down-button';
import { showAppPromotion } from '../../../helpers/AppPromotionHelper';
import { checkIfChatAnswered } from '../../../helpers/Chat';
import {
    groupTapFeedExperienceBasedQuestions,
    showExperienceBasedQuestions,
} from '../../../helpers/ExperianceBasedQuestionsHelper';
import { getCommonRoutePath, isGlobalVersionTwo, isIos, trackGetStarted } from '../../../helpers/Helpers';
import { bindDialog } from '../../../helpers/pusherHelper';
import { localStorageWrapper, sessionStorageWrapper } from '../../../helpers/storageFactory';
import {
    ACCOUNT,
    OPEN_NOTIFICATION_METHOD_POPUP,
    SAVE_UNIVERSITY_QUESTIONS,
    SET_FIRST_MESSAGE,
    UNIVERSITY_QUESTIONS,
    startRegistration,
} from '../../../redux/actions/accountActions';
import { CLOSE_ACTION_MODAL_CHILD, OPEN_ACTION_MODAL_CHILD } from '../../../redux/actions/componentsActions';
import { CHAT_INFO } from '../../../redux/actions/currentChatActions';
import { ADD_NEW_DIALOG_TO_IDS, CLOSE_AMBASSADOR_DIALOG, CLOSE_DIALOG } from '../../../redux/actions/dialogsActions';
import {
    REPLY_MESSAGE,
    SEND_AMBASSADOR_MESSAGE,
    SEND_MESSAGE,
    SHOW_OPTIONAL_QUESTIONS,
} from '../../../redux/actions/messagesActions';
import MixPanelService from '../../../system/services/MixPanelService';
import CloseFeedbackPopup from '../components/popups/CloseFeedbackPopup';
import AppPromotionMessage from './blocks/AppPromotionMessage';
import GroupMessagingHeader from './blocks/GroupMessagingHeader';
import MessagingBody from './blocks/MessagingBody';
import MessagingHeader from './blocks/MessagingHeader';
import ReplyMessageBlock from './blocks/ReplyMessage';

const Messages = ({
    mandatoryQuestionsAnswered,
    openMandatoryQuestionsPopup,
    showAcceptTerms,
    isMobile,
    handleGoBackMobile,
    hideMessagesBlockMobile,
}) => {
    const dispatch = useDispatch();
    const params = useParams();

    if (hideMessagesBlockMobile) {
        return null;
    }

    const account = useSelector((state) => state.account);
    const universities = useSelector((state) => state.widget.universities);
    const currentChat = useSelector((state) => state.currentChat);
    const experienceBasedQuestions = useSelector((state) => state.experienceBasedQuestionsPopup);
    const messages = useSelector((state) => state.messages);
    const replyMessage = useSelector((state) => state.replyMessage);
    const dialogs = useSelector((state) => state.dialogs);

    const [openClosePopup, setOpenClosePopup] = useState(false);
    const [message, setMessage] = useState(null);
    const [textValue, setTextValue] = useState(null);

    const listenerRef = useRef({
        dispatch,
        account,
        currentChat,
        messages,
        replyMessage,
        showAcceptTerms,
        openMandatoryQuestionsPopup,
        mandatoryQuestionsAnswered,
    });

    useEffect(() => {
        listenerRef.current = {
            dispatch,
            account,
            currentChat,
            messages,
            replyMessage,
            showAcceptTerms,
            openMandatoryQuestionsPopup,
            mandatoryQuestionsAnswered,
        };
    }, [
        dispatch,
        account,
        currentChat,
        messages,
        replyMessage,
        showAcceptTerms,
        openMandatoryQuestionsPopup,
        mandatoryQuestionsAnswered,
    ]);

    useEffect(() => {
        const { chatsType, ambassador } = currentChat;
        const { dialog_id: dialogId } = params;
        const { account_info: accountInfo } = account;
        const isGroupExperienceBasedQuestions = showExperienceBasedQuestions();

        if (experienceBasedQuestions.askedQuestion && chatsType === 1) {
            sendMessage(experienceBasedQuestions.askedQuestion);
            dispatch({
                type: ACCOUNT.SAVE_EXPERIENCE_BASED_QUESTION,
                payload: {
                    questionToAsk: null,
                },
            });
        }
        let universityId = null;
        if (currentChat.firstChat.id === +dialogId) {
            universityId = currentChat.firstChat.university.id;
        } else if (dialogs.items && dialogs.items.some((item) => item.id === +dialogId)) {
            universityId = dialogs.items.find((item) => item.id === +dialogId).university_id;
        }

        if (
            accountInfo?.key === 'prospect' &&
            messages.items &&
            messages.items.length < 2 &&
            chatsType === 1 &&
            !experienceBasedQuestions.askedQuestion &&
            !experienceBasedQuestions.isSkipped
        ) {
            if (isGroupExperienceBasedQuestions) {
                const groupUniversity = universities.find((item) => item.id === universityId);
                const user = messages.items[0].user;

                groupTapFeedExperienceBasedQuestions({
                    university: groupUniversity,
                    id: user.id,
                    name: user.name,
                    account,
                    type: chatsType === 4 ? 'group-chat' : 'chat',
                });
            } else {
                dispatch({
                    type: ACCOUNT.OPEN_EXPERIENCE_BASED_QUESTIONS_POPUP,
                    payload: {
                        university: currentChat.university,
                        id: ambassador.id,
                        name: ambassador.name,
                    },
                });
            }
        }

        return () => {
            dispatch({
                type: ACCOUNT.SKIP_EXPERIENCE_BASED_QUESTION,
                payload: {
                    isSkipped: false,
                },
            });
        };
    }, []);

    useEffect(() => {
        const element = document.querySelector('#chat-body');
        if (element && element.scrollTop !== element.scrollHeight && messages.scrollDirection === 'top') {
            if (element && !messages.isBlockedScroll) {
                element.scrollTop = element.scrollHeight;
            } else if (element && messages && element.scrollHeight > messages.previousScrollHeight) {
                element.scrollTop = element.scrollHeight - messages.previousScrollHeight;
            }
        }
    }, [messages]);

    useEffect(() => {
        if (account.acceptedTermsAnswered && message) {
            dispatch({
                type: ACCOUNT.FIRE_ON_ACCEPTED_TERMS,
                payload: { accepted: false },
            });
            if (
                account.hasMandatoryQuestions &&
                !mandatoryQuestionsAnswered &&
                account.account_info &&
                !account.account_info.isAnsweredMandatoryQuestion &&
                ((messages &&
                    messages.items &&
                    messages.items.length > 1 &&
                    currentChat.chatsType === 1 &&
                    checkIfChatAnswered(messages)) ||
                    currentChat.chatsType === 4)
            ) {
                openMandatoryQuestionsPopup();
            } else if (
                !currentChat.hasAccepted &&
                !currentChat.universityQuestionsAnswered &&
                currentChat.university &&
                ((messages &&
                    messages.items &&
                    messages.items.length > 1 &&
                    currentChat.chatsType === 1 &&
                    checkIfChatAnswered(messages)) ||
                    currentChat.chatsType === 4)
            ) {
                const onSuccess = (questions = []) => {
                    if (questions.length === 0) {
                        sendMessage(message);
                    } else {
                        dispatch({
                            type: SHOW_OPTIONAL_QUESTIONS,
                            payload: { showOptionalQuestions: true },
                        });
                    }
                };
                dispatch({
                    type: UNIVERSITY_QUESTIONS,
                    payload: {
                        universityId: currentChat.university.id,
                        engagementId: currentChat.activeChat,
                        engagementType: currentChat.chatsType === 4 ? 'group-chat' : 'chat',
                    },
                    meta: { onSuccess },
                });
            } else {
                sendMessage(message);
            }
        }
    }, [account.acceptedTermsAnswered, message, currentChat]);

    const handleSendMessage = useCallback(
        (value) => {
            const { data: chatData, university } = currentChat;

            if (!value) return;

            if (!account.account_info || !account.account_info.key) {
                dispatch({
                    type: SET_FIRST_MESSAGE,
                    payload: value,
                });

                trackGetStarted(currentChat.university.id, 'text field', true);
                dispatch(
                    startRegistration({
                        university,
                        id: chatData.id,
                        name: chatData.name,
                        type: currentChat.chatsType === 4 ? 'group-chat' : 'chat',
                    })
                );
                return;
            }
            if (account.account_info.key === 'prospect') {
                sendProspectMessage(value);
            } else {
                sendAmbassadorMessage(value);
                setTextValue(value);
            }
        },
        [account, currentChat, dispatch]
    );

    const sendProspectMessage = useCallback(
        (value) => {
            const {
                account,
                currentChat,
                messages,
                showAcceptTerms,
                openMandatoryQuestionsPopup,
                mandatoryQuestionsAnswered,
            } = listenerRef.current;
            const { account_info: accountInfo, hasMandatoryQuestions } = account;

            if (
                accountInfo.key === 'prospect' &&
                (!accountInfo.mailing ||
                    !currentChat.university ||
                    !accountInfo.mailing.find((item) => item.id === currentChat.university.id))
            ) {
                showAcceptTerms(true);
                return;
            }

            if (
                hasMandatoryQuestions &&
                !mandatoryQuestionsAnswered &&
                accountInfo &&
                !accountInfo.isAnsweredMandatoryQuestion &&
                ((messages &&
                    messages.items &&
                    messages.items.length > 1 &&
                    currentChat.chatsType === 1 &&
                    checkIfChatAnswered(messages)) ||
                    currentChat.chatsType === 4)
            ) {
                openMandatoryQuestionsPopup();
                return;
            }
            setMessage(value);
            setTextValue(value);

            if (
                !currentChat.hasAccepted &&
                !currentChat.universityQuestionsAnswered &&
                currentChat.university &&
                ((messages &&
                    messages.items &&
                    messages.items.length > 1 &&
                    currentChat.chatsType === 1 &&
                    checkIfChatAnswered(messages)) ||
                    currentChat.chatsType === 4)
            ) {
                dispatch({
                    type: SAVE_UNIVERSITY_QUESTIONS,
                    payload: {
                        universityId: currentChat.university.id,
                        engagementId: currentChat.activeChat,
                        engagementType: currentChat.chatsType === 4 ? 'group-chat' : 'chat',
                        answers: [],
                    },
                });
            }
            sendMessage(value);
        },
        [dispatch, currentChat]
    );

    const sendMessage = useCallback((message) => {
        const { dispatch, account, currentChat, messages, replyMessage } = listenerRef.current;
        if (!currentChat.university || !currentChat.data.ambassadors[0]) {
            dispatch({
                type: OPEN_ACTION_MODAL_CHILD,
                payload: {
                    children: (
                        <div className="general-popup chat-block-popup" id="verification">
                            <div className="sub-title">This ambassador has deleted their account.</div>
                            <div className="chat-redirect-block">
                                If you still have questions, please start a new conversation with{' '}
                                <a href={'#' + getCommonRoutePath() + '/chat-to-us'} onClick={handleClosePopup}>
                                    another ambassador
                                </a>
                            </div>
                            <Button
                                type="submit"
                                className="el-btn general-action-button cancel"
                                onClick={handleClosePopup}
                            >
                                OK
                            </Button>
                        </div>
                    ),
                },
            });
            return;
        }

        const onSuccess = () => {
            if (messages && messages.items && currentChat.university) {
                if (messages.items.length === 1 && currentChat.chatsType !== 4) {
                    try {
                        const props = {
                            Name: account.account_info.name + ' ' + account.account_info.last_name,
                            university_id: currentChat.university.id,
                        };
                        const propsTwo = { ...props };
                        if (localStorageWrapper.getItem('cameFromData')) {
                            let eventName = null;
                            const cameFromData = JSON.parse(localStorageWrapper.getItem('cameFromData'));
                            if (!account.dialogIds || !account.dialogIds.length) {
                                if (cameFromData.type === 'profile') {
                                    eventName = 'First chatting point: Profile';
                                    props.Profile = cameFromData.from;
                                } else if (cameFromData.type === 'post') {
                                    eventName = 'First chatting point: Post';
                                    props['Post type'] = cameFromData.dataType;
                                    props.Post = cameFromData.from;
                                } else if (cameFromData.type === 'faq') {
                                    eventName = 'First chatting point: FAQ';
                                    props.Faq = cameFromData.from;
                                }
                                if (eventName) {
                                    MixPanelService.track(eventName, props);
                                }
                            }

                            propsTwo['Starting point'] =
                                cameFromData.type === 'profile'
                                    ? 'Profile ' + cameFromData.from
                                    : cameFromData.type === 'post'
                                    ? 'Post'
                                    : 'FAQ';

                            MixPanelService.track('Started a conversation', propsTwo);
                            localStorageWrapper.removeItem('cameFromData');
                        }
                    } catch (err) {
                        console.log(err);
                    }
                } else if (currentChat.chatsType === 4) {
                    let userFound = false;
                    messages.items.map((message) => {
                        if (message.user && message.user.id === account.account_info.id) {
                            userFound = true;
                        }
                    });
                    if (!userFound) {
                        const props = {
                            Name: account.account_info.name + ' ' + account.account_info.last_name,
                            university_id: currentChat.university.id,
                        };
                        if (!account.dialogIds || !account.dialogIds.length) {
                            MixPanelService.track('First chatting point: Group chat', props);
                        }
                        props['Starting point'] = 'Group chat';
                        MixPanelService.track('Started a conversation', props);
                    }
                }
            }
            dispatch({
                type: REPLY_MESSAGE,
                payload: {
                    activeMessage: null,
                },
            });
            if (currentChat.data && !account.dialogIds.includes(+currentChat.data.id)) {
                dispatch({
                    type: ADD_NEW_DIALOG_TO_IDS,
                    payload: {
                        id: currentChat.data.id,
                    },
                });
                bindDialog(currentChat.data.id);
            }

            if (currentChat.chatsType === 4 && currentChat.data.members) {
                // if first message
                let memberFound = false;
                for (const member of currentChat.data.members) {
                    if (account.account_info && account.account_info.id === member.id) {
                        memberFound = true;
                    }
                }
                if (!memberFound) {
                    dispatch({
                        type: CHAT_INFO,
                        payload: {
                            dialog_id: currentChat.data.id,
                        },
                    });
                }
            }
            setTextValue('');
        };

        dispatch({
            type: SEND_MESSAGE,
            payload: {
                type: 'text',
                text: message,
                dialog_id: currentChat.data.id,
                parentMessageId: replyMessage.activeMessage ? replyMessage.activeMessage.id : null,
                loaderDisabled: true,
            },
            onSuccess,
        });

        if (!account.notificationData) {
            dispatch({
                type: OPEN_NOTIFICATION_METHOD_POPUP,
            });
        }
    }, []);

    const sendAmbassadorMessage = useCallback((value) => {
        const { dispatch, currentChat, replyMessage } = listenerRef.current;
        if (
            !currentChat.data ||
            !currentChat.data.id ||
            (!currentChat.chatsType === 4 && (!currentChat.data.prospect || !currentChat.data.prospect.id))
        )
            return;

        const onSuccess = () => {
            dispatch({
                type: REPLY_MESSAGE,
                payload: {
                    activeMessage: null,
                },
            });
            setTextValue('');
        };
        dispatch({
            type: SEND_AMBASSADOR_MESSAGE,
            payload: {
                type: 'text',
                text: value,
                dialog_id: currentChat.data.id,
                parentMessageId: replyMessage.activeMessage ? replyMessage.activeMessage.id : null,
                loaderDisabled: true,
            },
            onSuccess,
        });
    }, []);

    const handleSurePopupClick = useCallback(
        (methodStr, subtitle, isFlag) => {
            const method = () => {
                switch (methodStr) {
                    case 'handleReopenDialog':
                        handleReopenDialog();
                        break;
                }
            };
            dispatch({
                type: OPEN_ACTION_MODAL_CHILD,
                payload: {
                    children: (
                        <div className="general-popup" id="verification">
                            <h2 className="modal-title">Are you sure?</h2>
                            {subtitle}
                            <div className={'btn-wrap confirm-ban-buttons' + (isFlag ? ' flag-close-buttons' : '')}>
                                <Button type="submit" className="el-btn general-action-button" onClick={method}>
                                    Confirm
                                </Button>
                                <Button
                                    type="submit"
                                    className="el-btn general-action-button cancel"
                                    onClick={handleClosePopup}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    ),
                },
            });
        },
        [dispatch]
    );

    const handleClosePopup = useCallback(() => {
        dispatch({
            type: CLOSE_ACTION_MODAL_CHILD,
        });
    }, [dispatch]);

    const handleReopenDialog = useCallback(() => {
        const dialog = listenerRef.current.currentChat;
        handleClosePopup();
        dispatch({
            type:
                account.account_info && account.account_info.key === 'univercity-student'
                    ? CLOSE_AMBASSADOR_DIALOG
                    : CLOSE_DIALOG,
            payload: {
                dialog_id: dialog.activeChat,
            },
        });
    }, [dispatch, account, currentChat]);

    const openFeedbackPopup = useCallback(() => {
        setOpenClosePopup(true);
    }, []);

    const handleGoBack = useCallback(() => {
        if (sessionStorageWrapper.getItem('goBack')) {
            sessionStorageWrapper.removeItem('goBack');
        }
    }, []);

    const onAppPromotionClickOnMobileDevice = useCallback(() => {
        const isIOS = isIos();
        if (isMobile) {
            if (isIOS) {
                window.location.href = APP_STORE_URL.APPLE;
            } else {
                window.location.href = APP_STORE_URL.ANDROID;
            }
        } else {
            dispatch({
                type: ACCOUNT.OPEN_MOBILE_APP_PROMOTION_POPUP,
            });
        }
    }, [dispatch]);

    const currentChatInfo = currentChat.data;

    if (messages.isLoading) {
        return (
            <div className="chat chat-loader">
                <div className="chat-block">
                    <Loader />
                </div>
            </div>
        );
    }

    if (!messages.items || (!messages.items.length && !currentChatInfo.id)) {
        return (
            <div className="chat chat-select-chat">
                <div className="chat-block">
                    <div className="chat-block-icon">
                        <svg className="icon icon-chat">
                            <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref={Sprite + '#chat'} />
                        </svg>
                        <h2 className="modal-title">Select a chat</h2>
                    </div>
                </div>
            </div>
        );
    }

    // currentChatInfo.members.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    const pathBack = getCommonRoutePath(true);
    const isAppPromoted = showAppPromotion();
    const displayPinnedMessages = !!messages.pinnedMessages?.length;
    const isScrollDownButtonVisible = messages.isScrollDownButtonVisible;

    return (
        <div id="chat" className={'chat ' + (openClosePopup ? 'blur' : '')}>
            <div className="chat-block">
                {currentChat.data && currentChat.data.id ? (
                    currentChat.chatsType === 1 && account && account.token ? (
                        <div
                            className={
                                'header-container' +
                                (!isMobile && isGlobalVersionTwo ? ' second-version-header-container' : '')
                            }
                        >
                            {isMobile && (
                                <div
                                    className="btn chat-back-btn"
                                    onClick={handleGoBackMobile}
                                    aria-label="Go back"
                                    title="Back"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="7.657"
                                        height="13.314"
                                        viewBox="0 0 7.657 13.314"
                                    >
                                        <path
                                            d="M7.071 5.314l4.95-4.95a1 1 0 1 1 1.414 1.414L7.778 7.435a1 1 0 0 1-1.414 0L.707 1.778A1 1 0 1 1 2.121.364l4.95 4.95z"
                                            className="cls-1"
                                            transform="rotate(90 4.071 3.657)"
                                        />
                                    </svg>
                                </div>
                            )}
                            {!isMobile && isGlobalVersionTwo && (
                                <NavLink
                                    end
                                    to={pathBack}
                                    className="btn chat-back-btn"
                                    onClick={handleGoBack}
                                    aria-label="Go back"
                                    title="Back"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="7.657"
                                        height="13.314"
                                        viewBox="0 0 7.657 13.314"
                                    >
                                        <path
                                            d="M7.071 5.314l4.95-4.95a1 1 0 1 1 1.414 1.414L7.778 7.435a1 1 0 0 1-1.414 0L.707 1.778A1 1 0 1 1 2.121.364l4.95 4.95z"
                                            className="cls-1"
                                            transform="rotate(90 4.071 3.657)"
                                        />
                                    </svg>
                                </NavLink>
                            )}
                            <MessagingHeader
                                currentChat={currentChat}
                                openFeedbackPopup={openFeedbackPopup}
                                isMobile={isMobile}
                            />
                        </div>
                    ) : (
                        <div
                            className={
                                'header-container' +
                                (!isMobile && isGlobalVersionTwo ? ' second-version-header-container' : '')
                            }
                        >
                            {isMobile && (
                                <div
                                    className="btn chat-back-btn"
                                    onClick={handleGoBackMobile}
                                    aria-label="Go back"
                                    title="Back"
                                    role="button"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="8"
                                        height="14"
                                        viewBox="0 0 7.657 13.314"
                                    >
                                        <path
                                            d="M7.071 5.314l4.95-4.95a1 1 0 1 1 1.414 1.414L7.778 7.435a1 1 0 0 1-1.414 0L.707 1.778A1 1 0 1 1 2.121.364l4.95 4.95z"
                                            className="cls-1"
                                            transform="rotate(90 4.071 3.657)"
                                        />
                                    </svg>
                                </div>
                            )}
                            {!isMobile && isGlobalVersionTwo && (
                                <NavLink
                                    end
                                    to={pathBack}
                                    className="btn chat-back-btn"
                                    onClick={handleGoBack}
                                    aria-label="Go back"
                                    title="Back"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="7.657"
                                        height="13.314"
                                        viewBox="0 0 7.657 13.314"
                                    >
                                        <path
                                            d="M7.071 5.314l4.95-4.95a1 1 0 1 1 1.414 1.414L7.778 7.435a1 1 0 0 1-1.414 0L.707 1.778A1 1 0 1 1 2.121.364l4.95 4.95z"
                                            className="cls-1"
                                            transform="rotate(90 4.071 3.657)"
                                        />
                                    </svg>
                                </NavLink>
                            )}
                            <GroupMessagingHeader currentChat={currentChat} />
                        </div>
                    )
                ) : (
                    <div className="chat-head chat-empty-head"></div>
                )}
                {displayPinnedMessages && (
                    <PinnedMessageContainer dialogId={currentChat.activeChat} chatType={currentChat.chatsType} />
                )}

                {currentChat.data && currentChat.data.id && (
                    <MessagingBody
                        currentChat={currentChat}
                        messages={messages}
                        mandatoryQuestionsAnswered={mandatoryQuestionsAnswered}
                        openMandatoryQuestionsPopup={openMandatoryQuestionsPopup}
                    />
                )}
                {isAppPromoted && <AppPromotionMessage onAppPromotionClick={onAppPromotionClickOnMobileDevice} />}

                <ReplyMessageBlock />

                <DialogFooter
                    dialog={currentChatInfo}
                    activeUsers={messages.activeUsers}
                    isProspect={account.account_info && account.account_info.key === 'prospect'}
                    isNotAllowedToChat={
                        account.account_info &&
                        account.account_info.key === 'univercity-student' &&
                        !account.isAllowedToChat
                    }
                    timeLimitReached={account.timeLimitReached}
                    isMobile={isMobile}
                    isAllowedToReopen={
                        account.account_info &&
                        ((account.account_info.key === 'univercity-student' && currentChatInfo.prospect) ||
                            account.account_info.key === 'prospect')
                    }
                    handleSurePopupClick={() => {
                        handleSurePopupClick('handleReopenDialog');
                    }}
                >
                    {isScrollDownButtonVisible && (
                        <ScrollDownButton dialogId={currentChat.activeChat} chatType={currentChat.chatsType} />
                    )}

                    <SendMessageForm textValue={textValue} handleSubmit={handleSendMessage} isMobile={isMobile} />
                </DialogFooter>
            </div>

            <Modal
                open={openClosePopup}
                centered={true}
                footer={null}
                closable={false}
                width={430}
                className="notification-popup"
                maskClosable={false}
            >
                {openClosePopup ? (
                    <FocusTrap>
                        <CloseFeedbackPopup
                            dialog={currentChatInfo}
                            handleCloseReport={() => setOpenClosePopup(false)}
                        />
                    </FocusTrap>
                ) : (
                    false
                )}
            </Modal>
        </div>
    );
};

export default Messages;
